import React, { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { fg } from '@atlaskit/platform-feature-flags';

import type { GlobalSideNavigationProps, ItemsConfig } from '../../common/types';

import { useForYouNavItem } from './use-for-you-nav-item';
import { useHydratedShortcuts } from './use-hydrated-shortcuts';
import { useRecentNavItem } from './use-recent-nav-item';
import { useStarredNavItem } from './use-starred-nav-item';

export type GlobalItemsConfig = Map<keyof ItemsConfig, ItemsConfig[keyof ItemsConfig]>;

export type ItemsAndShortcuts = Pick<
	GlobalSideNavigationProps,
	'growthItemShortcuts' | 'items' | 'shortcuts'
> & {
	allItems: ItemsConfig;
	// todo: Migrate this to ItemsConfig?
	globalItems: GlobalItemsConfig;
};

export const ItemsAndShortcutsContext = createContext<ItemsAndShortcuts | undefined>(undefined);

export type ItemsAndShortcutsProviderProps = PropsWithChildren<
	Pick<GlobalSideNavigationProps, 'forYou' | 'growthItemShortcuts' | 'items'> & {
		rawShortcuts: GlobalSideNavigationProps['shortcuts'];
	}
>;

// Provides all items and shortcuts.
// If these items are truly static, they could be folded into `OptionsProvider` instead.
// If any items are not static, they may need their own context to avoid impacting the consumers of the other items.
export const ItemsAndShortcutsProvider = ({
	children,
	forYou,
	growthItemShortcuts,
	items,
	rawShortcuts,
}: ItemsAndShortcutsProviderProps) => {
	const forYouNavItem = useForYouNavItem(forYou);
	const recentNavItem = useRecentNavItem(forYou);
	const starredNavItem = useStarredNavItem(forYou);

	const globalItems = useMemo(
		(): GlobalItemsConfig => new Map([...forYouNavItem, ...recentNavItem, ...starredNavItem]),
		[forYouNavItem, recentNavItem, starredNavItem],
	);

	const allItems = useMemo(
		(): ItemsConfig => ({ ...Object.fromEntries(globalItems), ...items }),
		[globalItems, items],
	);

	const shortcuts = fg('navx-1742-use-hydrated-shortcuts')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useHydratedShortcuts(rawShortcuts)
		: rawShortcuts;

	const itemsAndShortcuts = useMemo(
		(): ItemsAndShortcuts => ({
			allItems,
			globalItems,
			growthItemShortcuts,
			items,
			shortcuts,
		}),
		[allItems, globalItems, growthItemShortcuts, items, shortcuts],
	);

	return (
		<ItemsAndShortcutsContext.Provider value={itemsAndShortcuts}>
			{children}
		</ItemsAndShortcutsContext.Provider>
	);
};

export const useItemsAndShortcuts = () => {
	const itemsAndShortcuts = useContext(ItemsAndShortcutsContext);

	invariant(
		itemsAndShortcuts,
		'useItemsAndShortcuts must be used inside an initialized ItemsAndShortcutsProvider',
	);

	return itemsAndShortcuts;
};
